import { useId, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { useReducedMotion } from "framer-motion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { Card } from "@/shared/ui/card";
import { cn } from "@/shared/lib/utils";
import { CompletenessBadge } from "./CompletenessBadge";
import { NotesPillButton, NotesBody } from "./NotesAffordance";
import type { CompletenessBadge as CompletenessBadgeData } from "./types";

interface SectionShellProps {
  title?: string;
  meta?: string;
  completeness?: CompletenessBadgeData;
  /** Optional panel notes. Renders a "NOTES" pill next to the title/badges;
   *  clicking reveals a muted prose block directly under the title bar
   *  (collapsed by default). */
  notes?: string;
  defaultOpen?: boolean;
  /** Rendered at the right of the header row (e.g. the Save button). */
  headerAccessory?: ReactNode;
  children: ReactNode;
}

/**
 * Wraps a section in a Collapsible card with a header row (caret + title +
 * optional count meta + optional completeness badge + optional notes pill).
 * The body collapses smoothly via the `collapsible-down/up` keyframes
 * registered in tailwind config.
 *
 * For untitled sections (kpi_row) we render the bare Card without the header.
 */
export function SectionShell({
  title,
  meta,
  completeness,
  notes,
  defaultOpen = true,
  headerAccessory,
  children,
}: SectionShellProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [notesOpen, setNotesOpen] = useState(false);
  const notesId = useId();
  const reduceMotion = useReducedMotion();

  if (!title) {
    return <Card className="overflow-hidden">{children}</Card>;
  }

  const titleGroup = (
    <>
      {/* `truncate` sets overflow:hidden, which html2canvas (PDF export)
          clips at the padding box — shearing off glyph descenders
          (g/y/p) of the title. pb-1 extends that clip box so descenders
          survive; -mb-1 cancels the 4px in the live layout so header
          height is unchanged on screen. */}
      <h3 className="truncate pb-1 -mb-1 text-sm font-semibold tracking-tight">{title}</h3>
      {meta ? (
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {meta}
        </span>
      ) : null}
      {completeness ? <CompletenessBadge {...completeness} /> : null}
    </>
  );

  const chevron = (
    <ChevronDown
      className={cn(
        "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
        open ? "rotate-0" : "-rotate-90",
      )}
    />
  );

  return (
    <Card className="group/shell overflow-hidden">
      <Collapsible open={open} onOpenChange={setOpen}>
        {notes ? (
          /* Notes variant: the header splits into two collapse-trigger
             segments so the notes pill can be a TRUE sibling <button> (a
             control nested inside the trigger button would be invalid HTML
             and unreliable for AT). The chevron segment is a second Radix
             trigger — same toggle, removed from the tab order so the collapse
             control stays a single tab stop. */
          <div className="flex items-center">
            <CollapsibleTrigger
              className={cn(
                "group flex items-baseline gap-3 min-w-0 py-3.5 pl-5 text-left transition-colors",
                "hover:bg-accent/40 focus-visible:outline-none focus-visible:bg-accent/40",
              )}
            >
              {titleGroup}
            </CollapsibleTrigger>
            <div className="pl-3">
              <NotesPillButton
                open={notesOpen}
                onToggle={() => setNotesOpen((o) => !o)}
                controlsId={notesId}
              />
            </div>
            <CollapsibleTrigger
              tabIndex={-1}
              aria-hidden="true"
              className={cn(
                "flex flex-1 items-center justify-end gap-2 self-stretch py-3.5 pr-5 transition-colors",
                "hover:bg-accent/40 focus-visible:outline-none",
              )}
            >
              {chevron}
            </CollapsibleTrigger>
            {headerAccessory ? <div className="pr-3">{headerAccessory}</div> : null}
          </div>
        ) : (
          <div className="flex items-center">
            <CollapsibleTrigger
              className={cn(
                "group flex flex-1 items-center justify-between gap-2 px-5 py-3.5 text-left transition-colors",
                "hover:bg-accent/40 focus-visible:outline-none focus-visible:bg-accent/40",
              )}
            >
              <div className="flex items-baseline gap-3 min-w-0">{titleGroup}</div>
              {chevron}
            </CollapsibleTrigger>
            {headerAccessory ? <div className="pr-3">{headerAccessory}</div> : null}
          </div>
        )}
        <CollapsibleContent
          className={cn(
            "overflow-hidden",
            // Skip the height keyframes under reduced-motion: the content still
            // shows/hides, just without the animated reveal (B4 rule 5 / D15).
            !reduceMotion &&
              "data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up",
          )}
        >
          {/* Notes sit inside CollapsibleContent so collapsing the panel also
              hides an expanded notes block. */}
          {notes ? <NotesBody id={notesId} open={notesOpen} body={notes} /> : null}
          <div className="border-t border-border">{children}</div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
